import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../Button/Button";
import SingleDatePicker from "../../SingleDatePicker/SingleDatePicker";
import CalendarIcon from "../../../assets/svg/calendar-icon";
import {
  clearPlaygroundLogsCreatedOn,
  selectPlaygroundLogsFilters,
  setPlaygroundLogsCreatedOn,
} from "../../../redux/slices/filterSlice";
import { endOfDay, format, startOfDay } from "date-fns";

const MAX_MONTH_RANGE = 6;

const exceedsSixMonths = (from: Date, to: Date) => {
  const sixMonthsFromStart = new Date(from);
  sixMonthsFromStart.setMonth(sixMonthsFromStart.getMonth() + MAX_MONTH_RANGE);
  return to > sixMonthsFromStart;
};

const LogsCreatedOnFilter = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { createdOn } = useSelector(selectPlaygroundLogsFilters);

  const [fromDate, setFromDate] = useState<Date | null>(
    createdOn.value.from ? new Date(createdOn.value.from) : null
  );
  const [toDate, setToDate] = useState<Date | null>(
    createdOn.value.to ? new Date(createdOn.value.to) : null
  );
  const [openCalendar, setOpenCalendar] = useState<"from" | "to" | null>(null);
  const [isRangeTooLong, setIsRangeTooLong] = useState(false);

  const hasChanges = useMemo(() => {
    const lastFrom = createdOn.value.from
      ? format(new Date(createdOn.value.from), "yyyy-MM-dd")
      : "";
    const lastTo = createdOn.value.to
      ? format(new Date(createdOn.value.to), "yyyy-MM-dd")
      : "";
    const currentFrom = fromDate ? format(fromDate, "yyyy-MM-dd") : "";
    const currentTo = toDate ? format(toDate, "yyyy-MM-dd") : "";

    return lastFrom !== currentFrom || lastTo !== currentTo;
  }, [createdOn.value.from, createdOn.value.to, fromDate, toDate]);

  const validateRange = useCallback((from: Date | null, to: Date | null) => {
    if (from && to) {
      setIsRangeTooLong(exceedsSixMonths(from, to));
    } else {
      setIsRangeTooLong(false);
    }
  }, []);

  const handleDateChange = (date: Date | null, isFromDate: boolean) => {
    const now = new Date();
    if (date && date > now) return;

    if (isFromDate) {
      if (date && toDate && date > toDate) return;
      setFromDate(date);
      validateRange(date, toDate);
    } else {
      if (date && fromDate && date < fromDate) return;
      const endOfDayDate = date ? endOfDay(date) : null;
      setToDate(endOfDayDate);
      validateRange(fromDate, endOfDayDate);
    }
  };

  const handleApply = () => {
    if (!fromDate || !toDate || isRangeTooLong) return;

    dispatch(
      setPlaygroundLogsCreatedOn({
        value: {
          from: startOfDay(fromDate).toISOString(),
          to: endOfDay(toDate).toISOString(),
        },
        displayValue: {
          from: fromDate.toISOString(),
          to: toDate.toISOString(),
        },
      })
    );
    onClose();
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setIsRangeTooLong(false);
    dispatch(clearPlaygroundLogsCreatedOn());
    onClose();
  };

  return (
    <div className="flex flex-col gap-3 mt-2 px-3">
      <p className="text-md text-heading-text dark:text-heading-text-dk">
        Pick the date range for log creation.
      </p>
      <div className="flex items-center gap-2">
        <SingleDatePicker
          label="From"
          labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
          Icon={CalendarIcon}
          value={fromDate}
          onChange={(date) => handleDateChange(date, true)}
          containerClassName="w-full"
          maxDate={toDate ? new Date(toDate) : new Date()}
          isOpen={openCalendar === "from"}
          onOpenChange={(isOpen) => setOpenCalendar(isOpen ? "from" : null)}
          inputClassName={`text-base font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk border px-[12px] py-[8px] rounded-[8px] ${
            isRangeTooLong
              ? "border-red-500 dark:border-red-500"
              : "border-border-gray dark:border-border-gray-dk"
          }`}
        />
        <SingleDatePicker
          label="To"
          labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
          Icon={CalendarIcon}
          value={toDate ? new Date(toDate) : null}
          onChange={(date) => handleDateChange(date, false)}
          containerClassName="w-full"
          minDate={fromDate ? new Date(fromDate) : undefined}
          maxDate={new Date()}
          isOpen={openCalendar === "to"}
          onOpenChange={(isOpen) => setOpenCalendar(isOpen ? "to" : null)}
          inputClassName={`text-base font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk border px-[12px] py-[8px] rounded-[8px] ${
            isRangeTooLong
              ? "border-red-500 dark:border-red-500"
              : "border-border-gray dark:border-border-gray-dk"
          }`}
        />
      </div>
      {isRangeTooLong && (
        <p className="text-sm text-red-500 dark:text-red-400 font-medium">
          Date range cannot exceed 6 months.
        </p>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          innerText="Clear"
          onClick={handleClear}
          disabled={!fromDate && !toDate}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          innerText="Apply"
          onClick={handleApply}
          disabled={!fromDate || !toDate || isRangeTooLong || !hasChanges}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default LogsCreatedOnFilter;
